/* eslint-disable @typescript-eslint/explicit-function-return-type */
import queryString from 'query-string';
import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import ReactPaginate from 'react-paginate';
import { NumberParam, useQueryParam, withDefault, ArrayParam, StringParam } from 'use-query-params';
import { Helmet } from 'react-helmet';
import ButtonList from '../checkbox/ButtonList';
import Content from './Content';
import { EOLocale } from 'eo-locale';
const Listing = ({ pageType, newsletterGuid, siteId, newsletterConfig, listNode, altFilter, filterDisabled, pagerDisabled, topN, }) => {
    const [selectedPage = 1, onSelectPage] = useQueryParam('page', withDefault(NumberParam, 1));
    const [selectedTypes, setSelectedTypes] = useQueryParam('type', withDefault(ArrayParam, []));
    const [selectedTopics, setSelectedTopics] = useQueryParam('topic', withDefault(ArrayParam, []));
    const [selectedIndustries, setSelectedIndustries] = useQueryParam('industry', withDefault(ArrayParam, []));
    const [searchQueryParam, setSearchQueryParam] = useQueryParam('text', StringParam);
    const isSearched = (searchQueryParam ?? '').trim().length > 0;
    const [orderParam, setOrderParam] = useQueryParam('order', StringParam);
    const [pendingTypes, setPendingTypes] = useState(selectedTypes.filter((f) => typeof f === 'string') || []);
    const [pendingTopics, setPendingTopics] = useState(selectedTopics.filter((f) => typeof f === 'string') || []);
    const [pendingIndustries, setPendingIndustries] = useState(selectedIndustries.filter((f) => typeof f === 'string') || []);
    const [appliedTypes, setAppliedTypes] = useState(selectedTypes.filter((f) => typeof f === 'string') || []);
    const [appliedTopics, setAppliedTopics] = useState(selectedTopics.filter((f) => typeof f === 'string') || []);
    const [appliedIndustries, setAppliedIndustries] = useState(selectedIndustries.filter((f) => typeof f === 'string') || []);
    const isFiltered = useMemo(() => (appliedTypes?.length ?? 0) +
        (appliedTopics?.length ?? 0) +
        (appliedIndustries?.length ?? 0) >
        0, [appliedTypes, appliedTopics, appliedIndustries]);
    const [searchText, setSearchText] = useState(searchQueryParam ?? '');
    const [searchOrder, setsearchOrder] = useState(orderParam ?? '');
    const [data, setData] = useState(null);
    const [shouldFilter, setShouldFilter] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const filterRef = useRef(null);
    useEffect(() => {
        getData();
        async function getData() {
            setIsLoading(true);
            const query = {
                page: selectedPage,
                type: selectedTypes && [...selectedTypes],
                topic: selectedTopics && [...selectedTopics],
                industry: selectedIndustries && [...selectedIndustries],
                text: searchText.trim(),
                order: searchOrder.trim(),
                listNode: listNode,
                altFilter,
                pagerDisabled,
                topN,
            };
            const apiName = getApiPathName(pageType);
            const res = await fetch(`/api/${apiName}/load/?${queryString.stringify(query)}`);
            if (!res.ok) {
                setIsLoading(false);
                return;
            }
            const resData = (await res.json());
            setData(resData);
            setShouldFilter(false);
            setIsLoading(false);
            if (isFirstLoad) {
                setIsFirstLoad(false);
            }
            if (filterRef.current) {
                const height = filterRef.current.offsetHeight;
                filterRef.current.style.bottom = `calc(-${height}px - 50px)`;
            }
            const filter = document.querySelector('.js-filter');
            const filterOverlay = document.querySelector('.js-filter-overlay');
            const filterButton = document.querySelector('.js-filter-button');
            const closeButton = filter?.querySelector('.js-close-button');
            if (filter && filterOverlay && filterButton && closeButton) {
                filterButton.addEventListener('click', () => {
                    filter.classList.add('filter--shown');
                    filterOverlay.classList.add('filter-overlay--shown');
                    filterButton.classList.add('filter-button--active');
                });
                closeButton.addEventListener('click', () => {
                    filter.classList.remove('filter--shown');
                    filterOverlay.classList.remove('filter-overlay--shown');
                    filterButton.classList.remove('filter-button--active');
                });
                document.addEventListener('mousedown', (event) => {
                    if (filterRef.current && !filterRef.current.contains(event.target)) {
                        filter.classList.remove('filter--shown');
                        filterOverlay.classList.remove('filter-overlay--shown');
                        filterButton.classList.remove('filter-button--active');
                    }
                });
                const filterContentWrapper = filter.querySelector('.js-filter__content-wrapper');
                if (filterContentWrapper) {
                    const isScrollable = filterContentWrapper.scrollHeight > filterContentWrapper.clientHeight;
                    if (isScrollable) {
                        filterContentWrapper.classList.add('scrollable');
                        filterContentWrapper?.addEventListener('scroll', () => {
                            const isScrolled = filterContentWrapper.scrollTop > 0;
                            const previousElement = filterContentWrapper.previousElementSibling;
                            if (previousElement) {
                                if (isScrolled) {
                                    filterContentWrapper.classList.add('scrolled');
                                    previousElement.classList.add('scrolled');
                                }
                                else {
                                    filterContentWrapper.classList.remove('scrolled');
                                    previousElement.classList.remove('scrolled');
                                }
                            }
                        });
                    }
                    else {
                        filterContentWrapper.classList.remove('scrollable');
                    }
                }
            }
            const searchWrapper = document.querySelector('.js-filter-search-wrapper');
            const searchInput = document.querySelector('.js-filter-search-input');
            const searchButton = document.querySelector('.js-filter-search-button');
            const searchClearButton = document.querySelector('.js-filter-search-clear-button');
            const orderSelectWrapper = document.querySelector('.js-filter-order-select-wrapper');
            if (searchWrapper && searchInput && searchButton && orderSelectWrapper && searchClearButton) {
                const activateSearch = () => {
                    searchWrapper.classList.add('filter-search-wrapper--active');
                    searchInput.classList.add('filter-search-input--active');
                    searchButton.classList.add('filter-search-button--active');
                    searchClearButton.classList.add('filter-search-clear-button--active');
                    orderSelectWrapper.classList.add('filter-order-select-wrapper--hidden-on-mobile');
                };
                searchInput.addEventListener('focus', activateSearch);
                searchInput.addEventListener('click', activateSearch);
                searchButton.addEventListener('click', () => {
                    searchWrapper.classList.remove('filter-search-wrapper--active');
                    searchInput.classList.remove('filter-search-input--active');
                    searchButton.classList.remove('filter-search-button--active');
                    orderSelectWrapper.classList.remove('filter-order-select-wrapper--hidden-on-mobile');
                    searchClearButton.classList.remove('filter-search-clear-button--active');
                });
                searchClearButton.addEventListener('click', () => {
                    searchWrapper.classList.remove('filter-search-wrapper--active');
                    searchInput.classList.remove('filter-search-input--active');
                    searchButton.classList.remove('filter-search-button--active');
                    orderSelectWrapper.classList.remove('filter-order-select-wrapper--hidden-on-mobile');
                    searchClearButton.classList.remove('filter-search-clear-button--active');
                });
                orderSelectWrapper.addEventListener('focusin', () => {
                    orderSelectWrapper.classList.add('filter-order-select-wrapper--active');
                });
                orderSelectWrapper.addEventListener('focusout', (e) => {
                    const next = e.relatedTarget;
                    if (!next || !orderSelectWrapper.contains(next)) {
                        orderSelectWrapper.classList.remove('filter-order-select-wrapper--active');
                    }
                });
            }
        }
    }, [shouldFilter, selectedPage, selectedTypes, selectedTopics, selectedIndustries]);
    const itemsPerPage = 11;
    const totalPages = Math.ceil((data?.totalResults ?? 0) / itemsPerPage);
    const CardSkeleton = () => (React.createElement("div", { className: "grid-column" },
        React.createElement("div", { className: "card card--skeleton" },
            React.createElement("div", { className: "card__media" }),
            React.createElement("div", { className: "card__content-external" },
                React.createElement("div", { className: "tag tag--blue" }),
                React.createElement("div", { className: "card__content-internal" },
                    React.createElement("div", { className: "card__title " }))))));
    return (React.createElement(Fragment, null,
        React.createElement(Helmet, null,
            selectedPage > 1 ? (React.createElement("link", { rel: "prev", href: `${window.location.pathname}?page=${selectedPage - 1}` })) : null,
            selectedPage < totalPages ? (React.createElement("link", { rel: "next", href: `${window.location.pathname}?page=${selectedPage + 1}` })) : null),
        !filterDisabled && (React.createElement("div", { className: "grid-container grid-container--full grid-container--indent" },
            React.createElement("div", { className: "grid-column" },
                React.createElement("div", { className: `filter-panel ${isLoading && isFirstLoad ? '' : 'filter-panel--shown'}` },
                    React.createElement("button", { className: `filter-button js-filter-button ${isFiltered ? 'filter-button--filtered' : ''}`, "data-tracking-code": "Filter_Show_Filter" },
                        React.createElement("svg", { className: "filter__icon icon-svg icon-filter-funnel" },
                            React.createElement("use", { xlinkHref: "/src/img/svg/icon-filter-funnel.svg#icon-filter-funnel" })),
                        React.createElement("span", { className: "filter-button__text" },
                            React.createElement(EOLocale.Text, { id: "xperience.filter.filterbutton" }))),
                    React.createElement("div", { className: `filter-search-wrapper js-filter-search-wrapper ${isSearched ? 'filter-search-wrapper--searched' : ''}` },
                        React.createElement("div", { className: "filter-search-input-wrapper" },
                            React.createElement("input", { id: "filter-search-input", type: "text", placeholder: "Search by title", value: searchText, onChange: (e) => setSearchText(e.target.value), onKeyDown: (e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        document
                                            .querySelector('.js-filter-search-button')
                                            ?.click();
                                    }
                                }, className: `form-control filter-search-input js-filter-search-input ${isFirstLoad ? '' : 'filter-search-input--shown'}`, "data-tracking-code": "Filter_Show_Search_Input" }),
                            React.createElement("button", { className: "btn btn--secondary filter-search-clear-button js-filter-search-clear-button", type: "button", onClick: () => {
                                    setSearchText('');
                                    setSearchQueryParam('', 'pushIn');
                                    onSelectPage(1, 'pushIn');
                                    setShouldFilter(true);
                                }, "data-tracking-code": "Filter_Clear_Search_Text" }, "\u2715")),
                        React.createElement("button", { className: "btn btn--secondary filter-search-button js-filter-search-button", id: "filter-search-button", type: "submit", onClick: () => {
                                onSelectPage(1, 'pushIn');
                                setSearchQueryParam(searchText, 'pushIn');
                                setShouldFilter(true);
                            }, "data-tracking-code": "Filter_Search_Text" },
                            React.createElement(EOLocale.Text, { id: "xperience.filter.searchbutton" }))),
                    React.createElement("div", { className: "filter-order-select-wrapper js-filter-order-select-wrapper" },
                        React.createElement("select", { className: "form-input form-input--select filter-order-select js-filter-order-select", value: searchOrder || 'newest', id: "filter-order-select", onChange: (e) => {
                                const selectedOrder = e.target.value;
                                setsearchOrder(selectedOrder);
                                setShouldFilter(true);
                                setOrderParam(selectedOrder, 'pushIn');
                            }, "data-tracking-code": "Filter_Order" },
                            React.createElement("option", { value: "newest" }, "Newest"),
                            React.createElement("option", { value: "oldest" }, "Oldest"))))))),
        React.createElement("div", { className: `grid-container grid-container--third grid-container--first-two ${isLoading ? 'cards--loading' : ''}` }, isLoading && isFirstLoad ? (Array.from({ length: 11 }).map((_, i) => React.createElement(CardSkeleton, { key: i }))) : data?.items?.length ? (React.createElement(Content, { items: data.items, newsletterGuid: newsletterGuid, siteId: siteId, newsletterConfig: newsletterConfig })) : (React.createElement("p", { className: "grid-column" },
            React.createElement(EOLocale.Text, { id: "xperience.filter.noresults" })))),
        React.createElement("div", { className: "js-filter-overlay filter-overlay" },
            React.createElement("div", { ref: filterRef, className: "filter js-filter" }, data?.filters?.topics &&
                renderFilters(Array.isArray(data?.filters?.topics) ? data.filters.topics : [data.filters.topics]))),
        !pagerDisabled && totalPages > 1 && (React.createElement("section", { className: "pagination" },
            React.createElement(ReactPaginate, { previousLabel: React.createElement("svg", { className: "icon-svg icon-chevron-down" },
                    React.createElement("use", { xlinkHref: "/src/img/svg/icon-chevron-down.svg#icon-chevron-down" })), nextLabel: React.createElement("svg", { className: "icon-svg icon-chevron-down" },
                    React.createElement("use", { xlinkHref: "/src/img/svg/icon-chevron-down.svg#icon-chevron-down" })), breakLabel: "...", breakClassName: "pagination__dots", pageCount: totalPages, marginPagesDisplayed: 2, pageRangeDisplayed: 5, onPageChange: (event) => {
                    onSelectPage(event.selected + 1, 'pushIn');
                }, forcePage: selectedPage - 1, hrefBuilder: (pageIndex) => `${window.location.pathname}?page=${pageIndex}`, activeClassName: "pagination--active", initialPage: selectedPage - 1 })))));
    // Map Filter group name to param key
    function getCategoryKey(name) {
        const n = (name || '').trim().toLowerCase();
        if (n === 'type')
            return 'type';
        if (n === 'topic' || n === 'topics')
            return 'topic';
        if (n === 'industry' || n === 'industries')
            return 'industry';
        return 'unknown';
    }
    function renderFilters(topicGroups) {
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: "filter__heading" },
                React.createElement("svg", { className: "filter__icon icon-svg icon-filter-funnel" },
                    React.createElement("use", { xlinkHref: "/src/img/svg/icon-filter-funnel.svg#icon-filter-funnel" })),
                React.createElement("h4", { className: "filter__heading-title" },
                    React.createElement(EOLocale.Text, { id: "xperience.filter.filterstitle" })),
                React.createElement("button", { className: "filter__close js-close-button", "data-tracking-code": "Filter_Close" })),
            React.createElement("div", { className: "filter__content-wrapper js-filter__content-wrapper" }, topicGroups
                .filter((group) => Array.isArray(group.filters) && group.filters.length > 0)
                .map((group) => {
                const key = getCategoryKey(group.name);
                // Choose pending state + setter based on category
                const selectedForGroup = key === 'type'
                    ? pendingTypes
                    : key === 'topic'
                        ? pendingTopics
                        : key === 'industry'
                            ? pendingIndustries
                            : [];
                const setPendingForGroup = key === 'type'
                    ? setPendingTypes
                    : key === 'topic'
                        ? setPendingTopics
                        : key === 'industry'
                            ? setPendingIndustries
                            : undefined;
                return (React.createElement("div", { key: group.name, className: "filter__content" },
                    React.createElement("h4", { className: "filter__content-title" }, group.name),
                    React.createElement("div", { className: "button-list button-list--on-white" },
                        React.createElement(ButtonList, { data: group.filters, name: group.name, selectedFilters: selectedForGroup, onChange: (event) => {
                                if (event.target instanceof HTMLInputElement && setPendingForGroup) {
                                    const currentItems = new Set(selectedForGroup);
                                    if (currentItems.has(event.target.value)) {
                                        currentItems.delete(event.target.value);
                                    }
                                    else {
                                        currentItems.add(event.target.value);
                                    }
                                    setPendingForGroup([...currentItems]);
                                }
                            } }))));
            })),
            React.createElement("div", { className: "filter__actions" },
                React.createElement("button", { className: "btn btn--secondary-black js-button-reset-filters", onClick: () => {
                        setPendingTypes([]);
                        setPendingTopics([]);
                        setPendingIndustries([]);
                    }, "data-tracking-code": "Filter_Clear_Filters" },
                    React.createElement(EOLocale.Text, { id: "xperience.filter.clearfilters" })),
                React.createElement("button", { className: "btn js-button-show-results", onClick: () => {
                        // Apply pendings → query params
                        setSelectedTypes(pendingTypes, 'pushIn');
                        setSelectedTopics(pendingTopics, 'pushIn');
                        setSelectedIndustries(pendingIndustries, 'pushIn');
                        // Keep local applied mirror for the "filtered" badge
                        setAppliedTypes(pendingTypes);
                        setAppliedTopics(pendingTopics);
                        setAppliedIndustries(pendingIndustries);
                        onSelectPage(1, 'pushIn');
                        setShouldFilter(true);
                        const filterOverlay = document.querySelector('.js-filter-overlay');
                        const filter = document.querySelector('.js-filter');
                        const filterButton = document.querySelector('.js-filter-button');
                        if (filter && filterButton && filterOverlay) {
                            filterOverlay.classList.remove('filter-overlay--shown');
                            filter.classList.remove('filter--shown');
                            filterButton.classList.remove('filter-button--active');
                        }
                    }, "data-tracking-code": "Filter_Show_Results" },
                    React.createElement(EOLocale.Text, { id: "xperience.filter.showresults" })))));
    }
};
export default Listing;
function getApiPathName(pageType) {
    switch (pageType) {
        case 'xperience.blogpost':
            return 'blog';
        case 'xperience.event':
            return 'event';
        case 'xperience.pressrelease':
            return 'pressrelease';
        case 'xperience.report':
            return 'report';
        case 'xperience.successstory':
            return 'successstory';
        case 'xperience.trainingcourse':
            return 'trainingcourse';
        case 'xperience.mention':
            return 'mention';
        default:
            throw new Error(`Unexpected page type: ${pageType}`);
    }
}
